import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import logger from "@/utils/logger";
import { toast } from "sonner";
import adminApi from "./adminApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, Eye, Trash2 } from "lucide-react";

const InquiriesManagement = () => {
  const { t } = useTranslation();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInquiries = useCallback(async () => {
    try {
      const response = await adminApi.get(`${API}/inquiries`);
      setInquiries(response.data);
    } catch (error) {
      logger.error("Error fetching inquiries:", error);
    } finally {
      setLoading(false);
    }
  }, [API, adminApi]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const markAsRead = async (inquiryId) => {
    try {
      await adminApi.put(`${API}/inquiries/${inquiryId}/read`);
      toast.success(t("admin.dashboard.toast.inquiryRead"));
      fetchInquiries();
    } catch {
      toast.error(t("admin.dashboard.toast.inquiryReadError"));
    }
  };

  const deleteInquiry = async (inquiryId) => {
    if (!window.confirm(t("admin.dashboard.confirmDelete.inquiry"))) return;
    try {
      await adminApi.delete(`${API}/inquiries/${inquiryId}`);
      toast.success(t("admin.dashboard.toast.inquiryDeleted"));
      fetchInquiries();
    } catch {
      toast.error(t("admin.dashboard.toast.inquiryDeleteError"));
    }
  };

  const renderContent = () => {
    if (loading) {
      return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
    }
    if (inquiries.length === 0) {
      return (
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Mail className="w-12 h-12 text-[#6B705C] mx-auto mb-4" />
            <p className="text-[#6B705C]">{t("admin.dashboard.noInquiries")}</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-4">
        {inquiries.map((inquiry) => (
          <Card
            key={inquiry.id}
            className={`bg-white border-0 shadow-sm ${!inquiry.is_read ? "border-l-4 border-l-[#E07A5F]" : ""}`}
            data-testid={`inquiry-card-${inquiry.id}`}
          >
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-[#3D405B]">{inquiry.name}</h3>
                    {!inquiry.is_read && (
                      <Badge className="bg-[#E07A5F] text-white">{t("admin.dashboard.newBadge")}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-[#6B705C] mb-1">{inquiry.email}</p>
                  {inquiry.phone && <p className="text-sm text-[#6B705C] mb-2">{inquiry.phone}</p>}
                  <p className="font-medium text-[#3D405B] mb-2">{inquiry.subject}</p>
                  <p className="text-[#6B705C]">{inquiry.message}</p>
                </div>
                <div className="flex gap-2">
                  {!inquiry.is_read && (
                    <Button variant="outline" size="sm" onClick={() => markAsRead(inquiry.id)} data-testid={`mark-read-${inquiry.id}`}>
                      <Eye className="w-4 h-4 mr-1" />
                      {t("admin.dashboard.actions.markRead")}
                    </Button>
                  )}
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => deleteInquiry(inquiry.id)}
                    className="text-red-600 hover:text-red-700"
                    data-testid={`delete-inquiry-${inquiry.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="inquiries-management" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#3D405B] font-['Playfair_Display']">{t("admin.dashboard.inquiries")}</h1>
        <p className="text-[#6B705C]">{t("admin.dashboard.manageInquiries")}</p>
      </div>
      {renderContent()}
    </div>
  );
};

export default InquiriesManagement;
